import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { SubscriptionTier } from '@/lib/pricing';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayOptions {
  tier: SubscriptionTier;
  billingPeriod: 'monthly' | 'annual';
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function useRazorpay() {
  const [loading, setLoading] = useState(false);
  const { user, profile } = useAuth();
  const { toast } = useToast();

  const loadRazorpayScript = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const initiatePayment = useCallback(async (options: RazorpayOptions) => {
    const { tier, billingPeriod, onSuccess, onError } = options;

    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to upgrade your plan.',
        variant: 'destructive',
      });
      return;
    }

    if (tier === 'free') {
      toast({
        title: 'Already Free',
        description: 'You are already on the free plan.',
      });
      return;
    }

    setLoading(true);

    try {
      // 1. Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // 2. Get session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // 3. Create Razorpay order
      const { data: orderData, error: orderError } = await supabase.functions.invoke(
        'create-razorpay-order',
        {
          body: { tier, billingPeriod },
        }
      );

      if (orderError || !orderData) {
        throw new Error(orderError?.message || 'Failed to create payment order');
      }

      const { orderId, amount, currency, keyId } = orderData;

      // 4. Open Razorpay checkout
      const razorpayOptions = {
        key: keyId,
        amount,
        currency,
        name: 'Sentinel',
        description: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan - ${billingPeriod === 'annual' ? 'Annual' : 'Monthly'}`,
        order_id: orderId,
        prefill: {
          name: profile?.name || '',
          email: user.email || '',
        },
        theme: {
          color: '#6366f1',
        },
        handler: async function (response: any) {
          try {
            // 5. Verify payment
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
              'verify-razorpay-payment',
              {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  tier,
                  amount,
                  billingPeriod,
                },
              }
            );

            if (verifyError || !verifyData?.success) {
              throw new Error(verifyError?.message || 'Payment verification failed');
            }

            toast({
              title: 'Upgrade Successful! 🎉',
              description: `Welcome to Sentinel ${tier.charAt(0).toUpperCase() + tier.slice(1)}!`,
            });

            onSuccess?.();
          } catch (error: any) {
            console.error('Payment verification error:', error);
            toast({
              title: 'Verification Failed',
              description: error.message || 'Please contact support.',
              variant: 'destructive',
            });
            onError?.(error.message);
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast({
              title: 'Payment Cancelled',
              description: 'You can try again anytime.',
            });
          },
        },
      };

      const razorpay = new window.Razorpay(razorpayOptions);
      razorpay.on('payment.failed', function (response: any) {
        console.error('Razorpay payment failed:', response.error);
        toast({
          title: 'Payment Failed',
          description: response.error.description || 'Please try again.',
          variant: 'destructive',
        });
        onError?.(response.error.description);
        setLoading(false);
      });
      razorpay.open();
    } catch (error: any) {
      console.error('Payment initiation error:', error);
      toast({
        title: 'Payment Error',
        description: error.message || 'Failed to initiate payment.',
        variant: 'destructive',
      });
      onError?.(error.message);
      setLoading(false);
    }
  }, [user, profile, toast, loadRazorpayScript]);

  return { initiatePayment, loading };
}
